import {ChevronBack, ChevronForward} from "@chayka/icons";
import clsx from "clsx";
import {useEffect, useMemo, useRef, useState} from "react";
import {Slider, type SliderHandle} from "../Slider";
import {ShadowBox} from "../ShadowBox";
import {
  addDays,
  DAY_OF_MONTH_FORMATTER,
  isDateInWeek,
  isSameDay,
  startOfDay,
  startOfWeek,
  formatWeekLabel,
  getWeekDays,
  WEEKDAY_FORMATTER,
} from "@chayka/utils";
import {XStack} from "../XStack";
import "../Calendar/shared.css";

const clampDateToRange = (date: Date, minDate?: Date, maxDate?: Date) => {
  if (minDate && date.getTime() < minDate.getTime()) {
    return minDate;
  }

  if (maxDate && date.getTime() > maxDate.getTime()) {
    return maxDate;
  }

  return date;
};

const clampWeekStartToRange = (weekStart: Date, minDate?: Date, maxDate?: Date) => {
  if (minDate && addDays(weekStart, 6).getTime() < minDate.getTime()) {
    return startOfWeek(minDate);
  }

  if (maxDate && weekStart.getTime() > maxDate.getTime()) {
    return startOfWeek(maxDate);
  }

  return weekStart;
};

const isDateDisabled = (date: Date, minDate?: Date, maxDate?: Date) => {
  if (minDate && date.getTime() < minDate.getTime()) {
    return true;
  }

  if (maxDate && date.getTime() > maxDate.getTime()) {
    return true;
  }

  return false;
};

const canNavigateToWeek = (weekStart: Date, minDate?: Date, maxDate?: Date) => {
  const weekEnd = addDays(weekStart, 6);

  if (minDate && weekEnd.getTime() < minDate.getTime()) {
    return false;
  }

  if (maxDate && weekStart.getTime() > maxDate.getTime()) {
    return false;
  }

  return true;
};

export interface CalendarSmallProps {
  value?: Date;
  defaultValue?: Date;
  onChange?: (date: Date) => void;
  onWeekChange?: (weekStart: Date, weekEnd: Date) => void;
  className?: string;
  disabled?: boolean;
  highlightedDates?: Date[];
  minDate?: Date;
  maxDate?: Date;
}

export const CalendarSmall: React.FC<CalendarSmallProps> = ({
  value,
  defaultValue,
  onChange,
  onWeekChange,
  className,
  disabled = false,
  highlightedDates,
  minDate,
  maxDate,
}) => {
  const today = useMemo(() => startOfDay(new Date()), []);
  const isControlled = value !== undefined;
  const controlledDate = value ? startOfDay(value) : undefined;
  const controlledDateTimestamp = controlledDate?.getTime();
  const minSelectableDate = useMemo(() => (minDate ? startOfDay(minDate) : undefined), [minDate]);
  const maxSelectableDate = useMemo(() => (maxDate ? startOfDay(maxDate) : undefined), [maxDate]);
  const [internalValue, setInternalValue] = useState<Date>(() =>
    clampDateToRange(
      startOfDay(defaultValue ?? value ?? today),
      minSelectableDate,
      maxSelectableDate,
    ),
  );
  const selectedDate = controlledDate ?? startOfDay(internalValue);
  const [visibleWeekStart, setVisibleWeekStart] = useState<Date>(() =>
    clampWeekStartToRange(
      startOfWeek(
        clampDateToRange(
          startOfDay(defaultValue ?? value ?? today),
          minSelectableDate,
          maxSelectableDate,
        ),
      ),
      minSelectableDate,
      maxSelectableDate,
    ),
  );
  const [isSliderAnimating, setIsSliderAnimating] = useState(false);
  const sliderRef = useRef<SliderHandle | null>(null);

  const weekPanels = useMemo(
    () =>
      [addDays(visibleWeekStart, -7), visibleWeekStart, addDays(visibleWeekStart, 7)].map(
        (weekStart) => getWeekDays(weekStart),
      ),
    [visibleWeekStart],
  );

  const canGoToPreviousWeek = useMemo(
    () => canNavigateToWeek(addDays(visibleWeekStart, -7), minSelectableDate, maxSelectableDate),
    [maxSelectableDate, minSelectableDate, visibleWeekStart],
  );

  const canGoToNextWeek = useMemo(
    () => canNavigateToWeek(addDays(visibleWeekStart, 7), minSelectableDate, maxSelectableDate),
    [maxSelectableDate, minSelectableDate, visibleWeekStart],
  );

  useEffect(() => {
    if (controlledDateTimestamp !== undefined) {
      const nextControlledDate = clampDateToRange(
        new Date(controlledDateTimestamp),
        minSelectableDate,
        maxSelectableDate,
      );

      setVisibleWeekStart((currentWeekStart) =>
        isDateInWeek(nextControlledDate, currentWeekStart)
          ? currentWeekStart
          : clampWeekStartToRange(
              startOfWeek(nextControlledDate),
              minSelectableDate,
              maxSelectableDate,
            ),
      );
    }
  }, [controlledDateTimestamp, maxSelectableDate, minSelectableDate]);

  useEffect(() => {
    setVisibleWeekStart((currentWeekStart) =>
      clampWeekStartToRange(currentWeekStart, minSelectableDate, maxSelectableDate),
    );

    if (!isControlled) {
      setInternalValue((currentValue) =>
        clampDateToRange(currentValue, minSelectableDate, maxSelectableDate),
      );
    }
  }, [isControlled, maxSelectableDate, minSelectableDate]);

  useEffect(() => {
    onWeekChange?.(visibleWeekStart, addDays(visibleWeekStart, 6));
  }, [onWeekChange, visibleWeekStart]);

  const updateSelectedDate = (date: Date) => {
    if (isDateDisabled(date, minSelectableDate, maxSelectableDate)) {
      return;
    }

    if (!isControlled) {
      setInternalValue(date);
    }

    onChange?.(date);
  };

  const navigateWeek = (direction: -1 | 1) => {
    setVisibleWeekStart((currentWeekStart) => {
      const nextWeekStart = addDays(currentWeekStart, direction * 7);

      return canNavigateToWeek(nextWeekStart, minSelectableDate, maxSelectableDate)
        ? nextWeekStart
        : currentWeekStart;
    });
  };

  return (
    <ShadowBox
      className={clsx(
        "calendar-surface p-4",
        disabled && "pointer-events-none opacity-60",
        className,
      )}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="calendar-title min-w-0 pl-2 text-center">
          {formatWeekLabel(visibleWeekStart)}
        </div>
        <XStack className="gap-2">
          <button
            type="button"
            className={clsx("calendar-nav-button active:opacity-60")}
            onClick={() => sliderRef.current?.navigate(-1)}
            aria-label="Show previous week"
            disabled={disabled || isSliderAnimating || !canGoToPreviousWeek}
          >
            <ChevronBack size={18} />
          </button>
          <button
            type="button"
            className={clsx("calendar-nav-button active:opacity-60")}
            onClick={() => sliderRef.current?.navigate(1)}
            aria-label="Show next week"
            disabled={disabled || isSliderAnimating || !canGoToNextWeek}
          >
            <ChevronForward size={18} />
          </button>
        </XStack>
      </div>

      <Slider
        ref={sliderRef}
        items={weekPanels}
        activeIndex={1}
        disabled={disabled}
        canSwipeNavigate={(direction) => (direction === -1 ? canGoToPreviousWeek : canGoToNextWeek)}
        onNavigate={navigateWeek}
        onAnimationStateChange={setIsSliderAnimating}
        panelClassName="grid grid-cols-7 gap-2 px-px"
        renderItem={({item: weekDays}) => (
          <>
            {weekDays.map((date) => {
              const isSelected = isSameDay(date, selectedDate);
              const isUnavailable = isDateDisabled(date, minSelectableDate, maxSelectableDate);
              const isHighlighted = highlightedDates?.some((highlightedDate) =>
                isSameDay(highlightedDate, date),
              );

              return (
                <div
                  key={date.toISOString()}
                  className={clsx(
                    "flex flex-col justify-center self-center items-center",
                    isUnavailable && "opacity-30",
                  )}
                >
                  <div className={clsx("calendar-weekday text-center")}>
                    {WEEKDAY_FORMATTER.format(date)}
                  </div>
                  <button
                    type="button"
                    className={clsx(
                      "calendar-day-button h-8 w-8 text-center text-sm",
                      isSelected && "calendar-day-button--selected",
                      isUnavailable && "calendar-day-button--disabled",
                    )}
                    style={
                      isSelected
                        ? {
                            background: "var(--color-primary)",
                            color: "var(--color-background)",
                          }
                        : undefined
                    }
                    onClick={() => updateSelectedDate(date)}
                    aria-pressed={isSelected}
                    disabled={disabled || isUnavailable}
                  >
                    <span>{DAY_OF_MONTH_FORMATTER.format(date)}</span>
                    {isHighlighted && (
                      <span
                        className={clsx(
                          "calendar-day-dot",
                          isSelected && "calendar-day-dot--selected",
                        )}
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </div>
              );
            })}
          </>
        )}
      />
    </ShadowBox>
  );
};
